import { h } from '@/lib/dom';
import { Table } from '@/components/Table';
import type { Column } from '@/components/Table';
import { SkeletonList } from '@/components/Skeleton';
import { Button } from '@/components/Button';
import { SyncBadge } from '@/components/SyncBadge';
import { data } from '@/lib/data';
import { formatDate, formatNumber, plural } from '@/lib/format';
import { currentSession } from '@/lib/session';
import type { RouteContext } from '@/router';
import { navigate } from '@/router';
import { pageLayout } from './layout';
import type { MeasurementRow } from '@/api/types';

type MetricDef = {
  label: string;
  unit: string;
  get: (r: MeasurementRow) => number | null;
};

const METRICS: MetricDef[] = [
  { label: 'Вес',    unit: 'кг', get: (r) => r.weight_kg },
  { label: 'Талия',  unit: 'см', get: (r) => r.waist_cm },
  { label: 'Грудь',  unit: 'см', get: (r) => r.chest_cm },
  { label: 'Бедро',  unit: 'см', get: (r) => r.thigh_cm },
  { label: 'Попа',   unit: 'см', get: (r) => r.hip_cm },
  { label: 'Бицепс', unit: 'см', get: (r) => r.biceps_cm },
  { label: 'Плечи',  unit: 'см', get: (r) => r.shoulders_cm },
];

function deltaClass(delta: number, isWeight: boolean): string {
  if (delta === 0) return 'mc-stat-delta mc-stat-delta--flat';
  if (isWeight) return delta < 0 ? 'mc-stat-delta mc-stat-delta--down' : 'mc-stat-delta mc-stat-delta--up';
  return delta < 0 ? 'mc-stat-delta mc-stat-delta--up' : 'mc-stat-delta mc-stat-delta--up';
}

function deltaText(delta: number): string {
  const sign = delta > 0 ? '+' : '';
  const arrow = delta > 0 ? ' ▲' : delta < 0 ? ' ▼' : '';
  return `${sign}${formatNumber(delta, 1)}${arrow}`;
}

function buildCard(row: MeasurementRow, prev: MeasurementRow | null): HTMLElement {
  const cells = METRICS.map((m) => {
    const val = m.get(row);
    const prevVal = prev ? m.get(prev) : null;
    const delta = val != null && prevVal != null ? val - prevVal : null;

    const valueEl = h('div', { class: 'mc-stat-value-row' }, [
      h('span', { class: 'mc-stat-value' }, val != null ? formatNumber(val, 1) : '—'),
      ...(val != null ? [h('span', { class: 'mc-stat-unit' }, m.unit)] : []),
    ]);

    const deltaEl = delta != null
      ? h('span', {
          class: deltaClass(delta, m.label === 'Вес'),
          'aria-label': `${delta > 0 ? 'плюс' : delta < 0 ? 'минус' : ''} ${Math.abs(delta).toFixed(1)}`,
        }, deltaText(delta))
      : null;

    return h('div', { class: 'mc-stat-cell' }, [
      h('span', { class: 'mc-stat-label' }, m.label),
      valueEl,
      ...(deltaEl ? [deltaEl] : []),
    ]);
  });

  return h('li', { class: 'measurement-card', role: 'listitem' }, [
    h('time', { class: 'mc-date', datetime: row.date }, formatDate(row.date)),
    h('div', { class: 'mc-stats-grid' }, cells),
  ]);
}

export async function renderMeasurements(_ctx: RouteContext): Promise<Node> {
  const user = currentSession();
  if (!user) {
    navigate('/login', { replace: true });
    return h('div');
  }

  const headerEl = h('header', { class: 'measurements-header' }, [
    h('div', { class: 'measurements-titles' }, [
      h('h1', { class: 'measurements-title' }, 'Измерения'),
      h('p', { class: 'measurements-subtitle' }, ' '),
    ]),
  ]);
  const contentEl = h('div', { class: 'measurements-body' }, SkeletonList(5));

  const root = pageLayout({
    user,
    active: 'measurements',
    children: [
      h('div', { class: 'measurements-page' }, [headerEl, contentEl]),
    ],
  });

  void (async () => {
    try {
      const bundle = await data.getHome();
      const summary = bundle.measurements;
      const payload = summary.payload;

      const rows = payload.rows ?? [];
      const latestDate = payload.latest_date;
      contentEl.innerHTML = '';

      if (!rows.length || payload.error) {
        const subtitleEl = headerEl.querySelector('.measurements-subtitle') as HTMLElement;
        subtitleEl.textContent = 'Записей пока нет';
        contentEl.appendChild(
          h('div', { class: 'empty-state', role: 'status' }, [
            h('p', null, 'Иван ведёт лог в Notion — измерение появится здесь автоматически.'),
          ]),
        );
        return;
      }

      const countLabel = plural(rows.length, ['запись', 'записи', 'записей']);
      const subtitleEl = headerEl.querySelector('.measurements-subtitle') as HTMLElement;
      subtitleEl.textContent = `${rows.length} ${countLabel} · последняя ${formatDate(latestDate)} · Notion`;

      if (!headerEl.querySelector('.measurements-sync')) {
        const syncWrap = h('div', { class: 'measurements-sync' }, SyncBadge(summary));
        headerEl.appendChild(syncWrap);
      }

      const columns: Column<MeasurementRow>[] = [
        { key: 'date',      header: 'Дата',        render: (r) => formatDate(r.date) },
        { key: 'weight_kg', header: 'Вес, кг',     align: 'right', render: (r) => r.weight_kg  != null ? formatNumber(r.weight_kg, 1)  : '—' },
        { key: 'waist_cm',  header: 'Талия, см',   align: 'right', render: (r) => r.waist_cm   != null ? formatNumber(r.waist_cm, 1)   : '—' },
        { key: 'chest_cm',  header: 'Грудь, см',   align: 'right', render: (r) => r.chest_cm   != null ? formatNumber(r.chest_cm, 1)   : '—' },
        { key: 'thigh_cm',  header: 'Бедро, см',   align: 'right', render: (r) => r.thigh_cm   != null ? formatNumber(r.thigh_cm, 1)   : '—' },
        { key: 'hip_cm',    header: 'Попа, см',    align: 'right', render: (r) => r.hip_cm     != null ? formatNumber(r.hip_cm, 1)     : '—' },
        { key: 'biceps_cm', header: 'Бицепс, см',  align: 'right', render: (r) => r.biceps_cm  != null ? formatNumber(r.biceps_cm, 1)  : '—' },
        { key: 'shoulders_cm', header: 'Плечи, см', align: 'right', render: (r) => r.shoulders_cm != null ? formatNumber(r.shoulders_cm, 1) : '—' },
      ];

      const tableEl = Table<MeasurementRow & Record<string, unknown>>({
        columns: columns as Column<MeasurementRow & Record<string, unknown>>[],
        rows: rows as Array<MeasurementRow & Record<string, unknown>>,
        ariaLabel: 'Таблица измерений тела',
      });

      const tableWrap = h('div', { class: 'measurements-table' }, tableEl);

      const cardList = h('ol', { class: 'measurement-card-list', role: 'list' },
        rows.map((row, i) => buildCard(row, i + 1 < rows.length ? rows[i + 1]! : null)),
      );

      contentEl.appendChild(tableWrap);
      contentEl.appendChild(cardList);
      document.dispatchEvent(new Event('route:settled'));
    } catch (err) {
      contentEl.innerHTML = '';
      contentEl.appendChild(
        h('div', { class: 'empty-state', role: 'alert' }, [
          h('p', null, 'Не удалось загрузить данные.'),
          Button({ label: 'Повторить', onClick: () => navigate('/measurements', { replace: true }) }),
        ]),
      );
      console.error(err);
    }
  })();

  return root;
}
